import { Injectable } from '@angular/core';
import { ConditionalFormattingTypes } from '@mode/shared/contract-common';
import { DialogService } from '@mode/shared/ui';

import { ConditionalFormattingDialogComponent } from './conditional-formatting-dialog.component';
import { Switch } from '@mode/shared/util-switch';

@Injectable({
  providedIn: 'root',
})
export class ConditionalFormattingModalComponentFacade {
  constructor(private dialogService: DialogService) {}

  public open(info: ConditionalFormattingTypes.ConditionalFormattingDialogData): void {
    let {
      colors,
      allColumns,
    }: { colors: ConditionalFormattingTypes.Color[]; allColumns: ConditionalFormattingTypes.Column[] } =
      info.format.flatTable.columns.reduce(
        (acc: any, column: any) => {
          const { id, colType, conditionalFormatting } = column;
          const colName = id.slice(1, id.length - 1);
          const isDimension = Switch.Attrs.detectDimension(colName, colType);

          // Show only measure fields
          if (!isDimension) {
            if (conditionalFormatting?.isEnabled) {
              const { legendId, color, addToText, selectedColorPalette } = conditionalFormatting;

              acc.colors.push({
                addToText,
                legendId,
                color,
                selected: {
                  name: colName,
                  value: id,
                },
                highlighted: false,
                selectedColorPalette,
              });
            }
            acc.allColumns.push({
              name: colName,
              value: id,
            });
          }
          return acc;
        },
        {
          colors: [],
          allColumns: [],
        }
      );

    if (info.selectedColumn) {
      const column = info.format.flatTable.columns.find((column: any) => info.selectedColumn === column.id);

      if (column) {
        const { id, colType } = column;
        const colName = id.slice(1, id.length - 1);
        const isDimension = Switch.Attrs.detectDimension(colName, colType);

        if (!this.isColumnAlreadyColored(colors, info.selectedColumn)) {
          if (!isDimension) {
            colors.push({
              addToText: false,
              legendId: '', // This value is updated in the CF dialog component
              selected: {
                name: info.selectedColumn.slice(1, info.selectedColumn.length - 1),
                value: info.selectedColumn,
              },
              highlighted: true,
              color: undefined, // This value is updated in the CF dialog component
              selectedColorPalette: undefined, // This value is updated in the CF dialog component
            });
          }
        } else {
          colors = colors.map((color: ConditionalFormattingTypes.Color) => {
            return {
              ...color,
              highlighted: info.selectedColumn === color.selected.value,
            };
          });
        }
      }
    }

    const data = {
      observables: info.observables,
      colors,
      allColumns,
    };

    const dialogRef = this.dialogService.open({
      component: ConditionalFormattingDialogComponent,
      matDialogConfig: {
        backdropClass: 'mode-filter-modal-backdrop',
        data,
      },
    });

    const confirmSubscription = dialogRef.componentInstance.apply.subscribe(
      (args: { colors: ConditionalFormattingTypes.Color[]; applyClicked: boolean }) => {
        const { colors, applyClicked } = args;
        if (info.applyAction) {
          info.applyAction(colors);
        }
        if (!applyClicked) {
          confirmSubscription.unsubscribe();
        }
      }
    );

    const cancelSubscription = dialogRef.componentInstance.cancel.subscribe(() => {
      if (info.cancelAction) {
        info.cancelAction();
      }
      cancelSubscription.unsubscribe();
      confirmSubscription.unsubscribe();
    });

    const backdropClickSubscription = dialogRef.backdropClick().subscribe(() => {
      if (info.cancelAction) {
        info.cancelAction();
      }
      backdropClickSubscription.unsubscribe();
      confirmSubscription.unsubscribe();
    });
  }

  private isColumnAlreadyColored(colors: ConditionalFormattingTypes.Color[] = [], selectedColumn: string): boolean {
    return colors.findIndex((color) => color.selected && color.selected.value === selectedColumn) >= 0;
  }
}
